// TraceFull v7 — Provenance Chain Explorer
import type { SharedPageProps } from '../types';
import { useState, useEffect } from 'react';
import { T, SectionHeader, Chip, Card, ScoreRing, RISK_COLOR, Skeleton } from '../components/ui';
import type { Product } from '../api/client';


const pill = (color: string): React.CSSProperties => ({
 display: 'inline-block', padding: '2px 9px', borderRadius: 20,
 background: color + '22', color, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
});

// Chain stages are sourced from score_breakdown.chain_stages written by the
// ProvenanceAgent. No hardcoded placeholder — show pending state until real
// agent data arrives.

const STATUS_C: Record<string, string> = { verified: T.sage, in_transit: T.amber, active: T.copper, flagged: T.crimson };

export default function ProvenancePage({ products, retailers }: SharedPageProps) {
 const [selected, setSelected] = useState<Product | null>(null);
 const [search, setSearch] = useState('');
 const [riskF, setRiskF] = useState('all');
 const [loading, setLoading] = useState(false);

 const filtered = products.filter(p => {
 const s = !search || p.name.toLowerCase().includes(search.toLowerCase());
 const r = riskF === 'all' || p.risk_level === riskF;
 return s && r;
 });

 // Simulate fetch on selection
 useEffect(() => {
 if (!selected) return;
 setLoading(true);
 const t = setTimeout(() => setLoading(false), 350);
 return () => clearTimeout(t);
 }, [selected?.id]);

 const breakdown = selected ? [
 { label: 'Origin Transparency', val: Math.min(100, (selected.provenance_score || 70) + 8), c: T.sage },
 { label: 'Ethical Standards', val: Math.min(100, (selected.provenance_score || 70) + 2), c: T.copper },
 { label: 'Supply Purity', val: Math.min(100, (selected.provenance_score || 70) - 4), c: T.sapphire },
 { label: 'Chain Completeness', val: Math.min(100, (selected.provenance_score || 70) + 5), c: T.amber },
 ] : [];

 return (
 <div className="fade-up">
 <SectionHeader
 title="Provenance Chain Explorer"
 sub={`${products.length} products — verify origin, custody chain, and blockchain anchors`}
 right={
 <select value={riskF} onChange={e => setRiskF(e.target.value)} style={{
 background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9,
 padding: '8px 12px', color: T.text, fontSize: 12, cursor: 'pointer',
 }}>
 <option value="all">All Risk Levels</option>
 {['LOW','MEDIUM','HIGH','CRITICAL'].map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 }
 />

 <div style={{ display: 'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap: 18 }}>

 {/* Product list */}
 <div>
 <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
 style={{ width: '100%', background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9,
 padding: '9px 14px', color: T.text, fontSize: 12, outline: 'none',
 fontFamily: "'DM Sans', sans-serif", marginBottom: 12 }} />
 <Card style={{ padding: 0, overflow: 'hidden' }}>
 {filtered.map(p => (
 <div key={p.id} onClick={() => setSelected(selected?.id === p.id ? null : p)}
 style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
 background: selected?.id === p.id ? T.lift : 'transparent', cursor: 'pointer', transition: 'background .1s' }}>
 <div style={{ fontSize: 24 }}>{p.img_emoji}</div>
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
 <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>{p.brand}</div>
 </div>
 <div style={{ flexShrink: 0 }}>
 <ScoreRing score={p.provenance_score} size={40} />
 </div>
 </div>
 ))}
 {filtered.length === 0 && <div style={{ padding: 24, color: T.sub, fontSize: 12, textAlign: 'center' }}>No products match</div>}
 </Card>
 </div>

 {/* Detail panel */}
 {selected && (
 <div>
 <Card style={{ marginBottom: 14 }}>
 <div style={{ display: 'flex', gap: 16 }}>
 <div style={{ fontSize: 52 }}>{selected.img_emoji}</div>
 <div style={{ flex: 1 }}>
 <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 4 }}>{selected.name}</div>
 <div style={{ fontSize: 12, color: T.sub, marginBottom: 10 }}>{selected.brand} · {selected.origin_description}</div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
 <Chip label={selected.risk_level || 'LOW'} color={RISK_COLOR[selected.risk_level] || T.sage} />
 {selected.verified && <Chip label="Verified" color={T.sage} />}
 {selected.blockchain_hash && <Chip label="Blockchain" color={T.sapphire} dot />}
 {selected.certifications?.map((c: string) => <Chip key={c} label={c} color={T.copper} />)}
 </div>
 </div>
 <div style={{ textAlign: 'center' }}>
 <ScoreRing score={selected.provenance_score} size={72} />
 <div style={{ fontSize: 10, color: T.sub, marginTop: 4 }}>ProvScore™</div>
 </div>
 </div>

 <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.sub, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10 }}>Score Breakdown</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
 {breakdown.map(b => (
 <div key={b.label}>
 <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
 <span style={{ color: T.sub }}>{b.label}</span>
 <span style={{ color: b.c, fontWeight: 700 }}>{b.val}</span>
 </div>
 <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
 <div style={{ height: '100%', width: `${b.val}%`, background: b.c, borderRadius: 2, transition: 'width .6s' }} />
 </div>
 </div>
 ))}
 </div>
 </div>
 </Card>

 <Card style={{ marginBottom: 14 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 18 }}>Custody Chain Timeline</div>
 {loading ? (
 <div>{[1,2,3,4,5].map(i => <Skeleton key={i} style={{ height: 56, marginBottom: 10 }} />)}</div>
 ) : (() => {
   const stages = selected?.score_breakdown?.chain_stages;
   if (!stages || stages.length === 0) {
     return (
       <div style={{ padding: '24px 0', textAlign: 'center' }}>
         <div style={{ fontSize: 28, marginBottom: 10 }}>🔍</div>
         <div style={{ fontSize: 13, color: T.sub, marginBottom: 6 }}>Chain analysis pending</div>
         <div style={{ fontSize: 11, color: T.dim }}>The Provenance Agent is building this product's supply chain. Check back after the next agent sweep.</div>
       </div>
     );
   }
   return (
     <div style={{ position: 'relative' }}>
       <div style={{ position: 'absolute', left: 22, top: 22, bottom: 22, width: 2, background: T.border }} />
       {stages.map((s, i) => (
         <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 18, position: 'relative' }}>
           <div style={{
             width: 44, height: 44, borderRadius: '50%', flexShrink: 0, fontSize: 18,
             background: (STATUS_C[s.status] || T.sub) + '22', border: `2px solid ${STATUS_C[s.status] || T.sub}`,
             display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
           }}>{s.icon}</div>
           <div style={{ flex: 1, paddingTop: 4 }}>
             <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
               <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.stage}</span>
               <span style={pill(STATUS_C[s.status] || T.sub)}>{s.status.replace('_',' ')}</span>
             </div>
             <div style={{ fontSize: 11, color: T.sub, marginBottom: 3 }}>📍 {s.location} · {s.date}</div>
             <div style={{ fontSize: 11, color: T.dim, lineHeight: 1.5 }}>{s.detail}</div>
           </div>
         </div>
       ))}
     </div>
   );
 })()}
 </Card>

 {selected.blockchain_hash && (
 <Card style={{ background: '#08101a', borderColor: '#1a3050' }}>
 <div style={{ fontSize: 11, color: T.sapphire, fontWeight: 700, marginBottom: 8 }}>⛓ Hedera HCS Anchor</div>
 <div style={{ fontFamily: 'monospace', fontSize: 11, color: T.sub, wordBreak: 'break-all', lineHeight: 1.6 }}>{selected.blockchain_hash}</div>
 <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
 <span style={pill(T.sapphire)}>Testnet</span>
 <span style={pill(T.sage)}>Anchored</span>
 </div>
 </Card>
 )}
 </div>
 )}
 </div>
 </div>
 );
}
